/**
 * Shared "end of 90-day trial" variant logic used by the Clinician and Practice
 * upgrade modals.
 *
 * Centralises the gating expression, team-admin / Stripe-managed checks, the
 * `release-end-of-90-day-trial-experience` exposure event, and the contact-sales
 * analytics handler so each consumer hook can stay focused on its plan-specific
 * concerns.
 */

import {
  IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED,
  IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED,
} from '@/components/constants/flags';
import { trialRecapModalMessages } from '@/features/trialsAndUpgrades/trial-recap-modal/messages';
import { openTrialUpgradeRequestCallbackModalAtom } from '@/features/trialsAndUpgrades/trial-upgrade-request-callback-modal/atoms';
import { useHasTrialUpgradeCallbackRequested } from '@/features/trialsAndUpgrades/trial-upgrade-request-callback-modal/useHasTrialUpgradeCallbackRequested';
import { useManualExposure } from '@/hooks/amplitude';
import { isPreviousTrialNinetyDayTrial } from '@heidi/commerce';
import type { TrackNinetyDayTrialContactSalesClick } from '@heidi/commerce/contracts';
import { useReverseTrial } from '@heidi/commerce/data';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { type MessageDescriptor, useIntl } from 'react-intl';

export interface UseNinetyDayTrialVariantArgs {
  /** Whether the consumer modal is currently open. Drives exposure tracking. */
  isOpen: boolean;
  /** When true, the variant is suppressed regardless of trial state (used by
   * the Clinician modal for `trialCTAMode`). */
  excludeFromVariant?: boolean;
  /**
   * The contact-sales emit, supplied by the modal that renders this variant.
   *
   * This used to be a `screenName` + `contactSalesAction` pair of app enums, and
   * that is what pulled the whole analytics trio into this hook. The two
   * consumers are the clinician and practice upgrade modals, which belong to
   * OTHER roots and report their own screens — so neither value was ever this
   * hook's to hold. It computes the plan-name label and nothing else; the
   * app-side binding (`trackClinicianUpgradeModalContactSalesClick` /
   * `trackPracticeUpgradeModalContactSalesClick`) names the screen and the
   * action (ruling 24).
   */
  trackContactSalesClick: TrackNinetyDayTrialContactSalesClick;
  /** Message descriptor that resolves to the plan display name (e.g.
   * `trialRecapModalMessages.clinicianPlanName`). */
  planNameMessage: MessageDescriptor;
  /** Called after the contact-sales analytics event fires so the consumer can
   * close its own modal. */
  onClose: () => void;
}

export function useNinetyDayTrialVariant({
  isOpen,
  excludeFromVariant = false,
  trackContactSalesClick,
  planNameMessage,
  onClose,
}: UseNinetyDayTrialVariantArgs) {
  const intl = useIntl();
  const {
    isFakeClinicianTrial,
    clinicianTrialEnded,
    previousStatusBeforeClinicianTrial,
  } = useReverseTrial();
  const {
    hasTeam,
    isTeamAdmin,
    isTeamStripeManaged,
    isNonAdminTeamMember,
    isLoadingTeamDetails,
  } = useCombinedQueriesTeamGeneralInfo();

  // Active 90-day trial users
  const isActiveNinetyDayTrial =
    IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED() &&
    !excludeFromVariant &&
    isFakeClinicianTrial;

  // True only when the post-90-day experience flag is on AND a PRO/PRO_PLUS user's Clinician
  // reverse trial has ended. Drives the post-trial-ended copy override on the upgrade modals.
  const isNinetyDayTrialEnded =
    IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED() &&
    !excludeFromVariant &&
    clinicianTrialEnded &&
    isPreviousTrialNinetyDayTrial(previousStatusBeforeClinicianTrial);

  // The 90-day variant layout (grouped pricing card, UpgradeTrialFeatureList,
  // UpgradeTermsFooter, etc.) is used for both active-final-week and trial-ended users;
  // the trial-ended branch additionally swaps in the post-trial title/subtitle/CTA copy.
  const showNinetyDayTrialVariant = isActiveNinetyDayTrial || isNinetyDayTrialEnded;

  const shouldDisableButtonForNonStripeManagedTeam = hasTeam && !isTeamStripeManaged;

  const isUpgradeDisabledForTeamMember =
    !isLoadingTeamDetails &&
    isNonAdminTeamMember &&
    IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED();

  const showNonAdminRequestBanner =
    showNinetyDayTrialVariant && isUpgradeDisabledForTeamMember;

  const showContactSalesInsteadOfUpgrade =
    showNinetyDayTrialVariant &&
    !isLoadingTeamDetails &&
    isTeamAdmin === true &&
    !isTeamStripeManaged;

  const planDisplayName = useMemo(
    () => intl.formatMessage(planNameMessage),
    [intl, planNameMessage]
  );

  const hasCallbackRequested = useHasTrialUpgradeCallbackRequested();
  const openTrialUpgradeRequestCallbackModal = useSetAtom(
    openTrialUpgradeRequestCallbackModalAtom
  );

  const handleContactSalesClick = useCallback(() => {
    if (hasCallbackRequested) return;
    trackContactSalesClick({
      label: intl.formatMessage(
        planNameMessage === trialRecapModalMessages.practicePlanName
          ? trialRecapModalMessages.talkToUsKeepPlanCtaPractice
          : trialRecapModalMessages.talkToUsKeepPlanCtaClinician
      ),
    });
    onClose();
    openTrialUpgradeRequestCallbackModal();
  }, [
    trackContactSalesClick,
    hasCallbackRequested,
    intl,
    planNameMessage,
    onClose,
    openTrialUpgradeRequestCallbackModal,
  ]);

  useManualExposure({
    flagKey: 'release-end-of-90-day-trial-experience',
    isEnabled: IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED(),
    isVisible: Boolean(isOpen && showNinetyDayTrialVariant),
  });

  return {
    showNinetyDayTrialVariant,
    isNinetyDayTrialEnded,
    isUpgradeDisabledForTeamMember,
    shouldDisableButtonForNonStripeManagedTeam,
    showNonAdminRequestBanner,
    showContactSalesInsteadOfUpgrade,
    planDisplayName,
    handleContactSalesClick,
    hasCallbackRequested,
  };
}
